import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DestroyableComponent } from '@models/destroyable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { filter, takeUntil } from 'rxjs/operators';
import { IStep } from './stepper.models';
import { StepperService, StepperState } from './stepper.service';

@Component({
  selector: 'app-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepperComponent extends DestroyableComponent implements OnInit {
  errorNotificationService = inject(ErrorNotificationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private stepperService = inject(StepperService);

  @Input() public steps: IStep[] = [];
  @Input() public activeIndex: number = 0;
  @Input() public withClick = false;
  @Output() public touched = new EventEmitter<number>();
  @Output() public highlightErrors = new EventEmitter<boolean>();
  @Output() public stepChanged = new EventEmitter<IStep>();

  private nextStepIndex: number = 0;
  public stepperState: StepperState;

  ngOnInit() {
    // Подписка на состояние stepper
    this.stepperService.stepperState$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (state) => {
        this.stepperState = state;
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });

    // Обновление состояния при изменении входных данных
    this.stepperService.setTotalSteps(this.steps.length);
    if (this.activeStep) {
      this.stepperService.setCurrentStep(this.activeStep.id);
    }

    this.stepperService.isOk$
      .pipe(filter((isOk) => isOk))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          if (this.nextStep && this.nextStep.touched) {
            this.highlightErrors.emit(!!this.nextStep.invalid);
          }

          if (this.nextStep && this.nextStep.route) {
            this.router.navigate([`./${this.nextStep.route}`], { relativeTo: this.route }).then(() => true);
            this.touched.emit(this.nextStep.id);
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public get activeStep(): IStep | undefined {
    return this.steps[this.activeIndex];
  }

  public get nextStep(): IStep | undefined {
    return this.steps[this.nextStepIndex];
  }

  public goToStep(event: Event, step: IStep): void {
    event.preventDefault();
    event.stopPropagation();

    // Проверяем, что шаг не отключен и не является текущим активным
    if (step.disabled || step.id === this.activeStep?.id) {
      return;
    }

    this.nextStepIndex = this.steps.findIndex((s) => s.id === step.id);

    // Если шаг не найден, выходим
    if (this.nextStepIndex === -1) {
      return;
    }

    // Обновляем состояние
    this.stepperService.setCurrentStep(step.id);

    // Помечаем шаг как посещенный
    step.touched = true;

    // Если у шага нет маршрута и включен режим клика
    if (!step.route && this.withClick) {
      this.touched.emit(step.id); // Эмитим событие touched
      this.stepChanged.emit(step);
      return;
    }

    // Если у шага есть маршрут и не включен режим клика
    if (step.route && !this.withClick) {
      this.router
        .navigate([step.route], { relativeTo: this.route })
        .then(() => {
          this.touched.emit(step.id);
        })
        .catch((error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        });
      return;
    }

    // Для остальных случаев используем сервис
    this.stepperService.goNextIsOk(true);
    this.touched.emit(step.id); // Эмитим событие touched
  }

  public trackByFn(index: number, step: IStep): number {
    return step.id;
  }

  public isStepActive(step: IStep): boolean {
    return step.id === this.activeStep?.id;
  }

  public isStepDisabled(step: IStep): boolean {
    return !!step.disabled;
  }

  public isStepInvalid(step: IStep): boolean {
    return !!step.invalid;
  }

  public isStepTouched(step: IStep): boolean {
    // Шаг считается "тронутым" только если пользователь действительно переходил на него
    // и он не является текущим активным шагом
    return !!step.touched && step.id !== this.activeStep?.id;
  }

  public isStepPassed(step: IStep): boolean {
    // Шаг считается "пройденным" только если он валиден и пользователь переходил на него
    return !step.invalid && !!step.touched;
  }

  public isStepVisited(step: IStep): boolean {
    // Проверяем, был ли шаг посещен пользователем
    return !!step.touched;
  }

  public canNavigateToStep(step: IStep): boolean {
    return this.stepperService.canNavigateToStep(step, this.activeStep?.id || 0);
  }

  public getProgressPercentage(): number {
    if (!this.steps.length) return 0;
    const passedSteps = this.steps.filter((step) => this.isStepPassed(step)).length;
    return Math.round((passedSteps / this.steps.length) * 100);
  }
}
